import { useSyncExternalStore } from "react";

/** What the reader asked for. Leaving it to the system is a choice too. */
export type ThemeChoice = "system" | "light" | "dark";

/** What is on the screen, once the system has had its say. */
type Theme = "light" | "dark";

/**
 * Where the choice is kept. `index.html` reads the same key before first
 * paint, which is what stops the page flashing the wrong theme on load. No
 * entry means the system decides, so choosing the system removes the key
 * rather than writing a third value.
 */
const storageKey = "pax-theme";
const systemPrefersDark = "(prefers-color-scheme: dark)";

const subscribers = new Set<() => void>();

function readChoice(): ThemeChoice {
  const stored = localStorage.getItem(storageKey);
  return stored === "light" || stored === "dark" ? stored : "system";
}

function resolve(choice: ThemeChoice): Theme {
  if (choice !== "system") return choice;
  return window.matchMedia(systemPrefersDark).matches ? "dark" : "light";
}

function showTheme(theme: Theme) {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

/**
 * `useSyncExternalStore` compares snapshots by identity, so the pair has to
 * stay the same object until one half of it actually changes.
 */
let snapshot: { choice: ThemeChoice; theme: Theme } = { choice: "system", theme: "light" };

function getSnapshot() {
  const choice = readChoice();
  const theme = resolve(choice);
  if (choice !== snapshot.choice || theme !== snapshot.theme) snapshot = { choice, theme };
  return snapshot;
}

/** Nothing is rendered on a server, so the light theme is a safe stand-in. */
const serverSnapshot = { choice: "system", theme: "light" } as const;

/** Switch the page over and remember it, here and in every other open tab. */
function chooseTheme(choice: ThemeChoice) {
  if (choice === "system") localStorage.removeItem(storageKey);
  else localStorage.setItem(storageKey, choice);
  showTheme(resolve(choice));
  for (const notify of subscribers) notify();
}

function subscribe(notify: () => void) {
  subscribers.add(notify);

  const system = window.matchMedia(systemPrefersDark);
  const followSystem = () => {
    if (readChoice() !== "system") return;
    showTheme(resolve("system"));
    notify();
  };
  const followOtherTabs = (event: StorageEvent) => {
    if (event.key !== storageKey) return;
    showTheme(resolve(readChoice()));
    notify();
  };

  system.addEventListener("change", followSystem);
  window.addEventListener("storage", followOtherTabs);

  return () => {
    subscribers.delete(notify);
    system.removeEventListener("change", followSystem);
    window.removeEventListener("storage", followOtherTabs);
  };
}

export function useTheme() {
  const { choice, theme } = useSyncExternalStore(subscribe, getSnapshot, () => serverSnapshot);
  return { choice, theme, chooseTheme };
}
